import {
  flow,
  isFinite,
  isDate,
  each,
  isRegExp,
  filter,
  isString,
  isNumber,
  keys,
  flatten,
  uniq,
} from 'lodash';
import { curriedMap, curriedTest } from '@core/lib/fp';

const _localStorage = window.localStorage;
const _sessionStorage = window.sessionStorage;

/**
 * @param {String} key
 * @param {String=} defaultValue
 * @returns {String}
 */
function get(key, defaultValue = null) {
  const item = self._getItem(key);

  if (item === null) {
    return defaultValue;
  }

  const json = { exp: null, ...JSON.parse(item) };

  if (_isExpired(json.exp)) {
    self.remove(key);
    return defaultValue;
  }

  return json.val;
}

/**
 * @returns {String[]}
 */
function _keys() {
  return flow(curriedMap(keys), flatten, uniq)([_localStorage, _sessionStorage]);
}

/**
 * @param {String} key
 * @returns {String}
 * @private
 */
function _getItem(key) {
  try {
    return _localStorage.getItem(key) || _sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

/**
 * @param {Number} expiration
 * @returns {Boolean}
 * @private
 */
function _isExpired(expiration) {
  if (expiration === null) {
    return false;
  }

  if (!isFinite(expiration)) {
    return true;
  }

  return expiration < Date.now();
}

/**
 * @param {String} key
 * @param {Any} value
 * @param {Date=} expiration
 * @returns {String} initial value that is saved in storage
 */
function put(key, value, expiration = null) {
  const payload = flow(val => ({ val }), self._addExpiration(expiration), JSON.stringify)(value);

  self._setItem(key, payload);
  return value;
}

/**
 * Adds the expiration to a payload if a valid date was provided
 *
 * @param {Date} expiration
 * @param {Object} payload
 * @returns {Object}
 * @private
 */
function _addExpiration(expiration) {
  return payload =>
    isDate(expiration) ?
      { ...payload, exp: expiration.getTime() } :
      payload;
}

/**
 * @param {String} key
 * @param {String} value
 * @private
 */
function _setItem(key, value) {
  try {
    _localStorage.setItem(key, value);
  } catch (e) {
    _sessionStorage.setItem(key, value);
  }
}

/**
 * @param {RegExp|String|Number} expression
 */
function remove(expression) {
  const toRemove = self._getMatchingKeys(expression);
  each(toRemove, self._removeItem);
}

/**
 * @param {RegExp|String|Number} expression
 * @returns {String[]}
 */
function _getMatchingKeys(expression) {
  if (isRegExp(expression)) {
    return filter(self._keys(), curriedTest(expression));
  }

  if (isString(expression) || isNumber(expression)) {
    return [String(expression)];
  }

  return [];
}

/**
 * @param {String} key
 * @private
 */
function _removeItem(key) {
  try {
    _localStorage.removeItem(key);
    _sessionStorage.removeItem(key);
  } catch (e) {} // eslint-disable-line no-empty
}

function clear() {
  self._clearAll();
}

/**
 * @private
 */
function _clearAll() {
  try {
    _localStorage.clear();
    _sessionStorage.clear();
  } catch (e) {} // eslint-disable-line no-empty
}

const self = {
  get,
  put,
  remove,
  clear,
  _keys,
  _getItem,
  _isExpired,
  _addExpiration,
  _setItem,
  _getMatchingKeys,
  _removeItem,
  _clearAll
};

export default self;
